import { Either, Maybe } from 'monet';

/**
 * @description Шлюз предложений по операциям в Панели клиники.
 * @param {Axios} axios Http клиент.
 * @constructor
 */
export default function SuggestionGateway(axios) {
  /**
   * @description Получить данные создания сотрудника.
   * @returns {Promise<object>}
   */
  this.getEmployeeCreation = () => axios
    .get('/api/suggestions/employee-creation')
    .then((response) => ({
      cabinetList: response.data.cabinets || [],
      confidentialityLevels: response.data.confidentiality_levels || [],
      employeeRoleList: response.data.employee_roles || [],
      employeeSpecialtyList: response.data.employee_specialties || [],
      hasEgisz: response.data.has_egisz || false,
      nsiPositionList: response.data.nsi_positions || [],
      permissionsByGroups: response.data.permission_groups || [],
      timeStepList: response.data.time_steps || [],
      lpus: response.data.egisz_lpus || [],
      notificationChannels: response.data.notification_channels || [],
    }));

  /**
   * @description Получить список доступных разделов.
   * @returns {Promise<string[]>}
   */
  this.getAvailableSections = async () => {
    const response = await axios.get('/api/suggestions/available-sections');
    return (response.data || []).map(({ slug }) => slug);
  };

  /**
   * @description Получить данные для создания пациента.
   * @returns {Promise<Maybe>}
   */
  this.getPatientCreation = () => axios
    .get('/api/suggestions/patient-creation')
    .then(
      async (response) => {
        const actions = response.data.data.actions || [];
        const addressTypeList = response.data.data['address-types'] || [];
        return Maybe.some({ actions, addressTypeList });
      },
      async (error) => {
        if (error.isAxiosError) {
          return Maybe.none();
        }
        throw error;
      },
    );

  /**
   * @description Получить опции настроек клиники.
   * @returns {Promise<Maybe>}
   */
  this.viewClinicSettingsOptions = () => axios
    .get('/api/suggestions/clinic-settings-options')
    .then(
      async (response) => {
        const {
          egisz_confidentialities: egiszConfidentialities,
          sno_options: snoOptions,
          vat_options: vatOptions,
          right_form_registrations_options: rightFormRegistrationsOptions,
        } = response.data;
        return Maybe.some({
          egiszConfidentialities,
          snoOptions,
          vatOptions,
          rightFormRegistrationsOptions,
        });
      },
      async (error) => {
        if (error.isAxiosError && error.response.status === 403) {
          return Maybe.none();
        }
        throw error;
      },
    );
  /**
   * @description Получить интеграции.
   * @returns {Promise<Maybe>}
   */
  this.viewAllIntegrations = () => axios
    .get('/api/integrations')
    .then(
      async (response) => Maybe.fromEmpty(response.data.data),
      async (error) => {
        if (error.isAxiosError) {
          return Maybe.none();
        }
        throw error;
      },
    );

  /**
   * @description Получить настройки таблицы.
   * @param {string} table
   * @returns {Promise<Maybe>}
   */
  this.viewTableSettings = (table) => axios
    .get(`/api/suggestions/tables/${table}`)
    .then(
      (response) => Maybe.fromEmpty(response.data.data),
      (error) => {
        if (error.isAxiosError) {
          return Maybe.none();
        }
        throw error;
      },
    );

  /**
   * @description Сохранить настройки таблицы.
   * @param {string} table
   * @param {any[]}  columns
   * @returns {Promise<Either>}
   */
  this.storeTableSettings = (table, columns) => axios
    .put(`/api/suggestions/tables/${table}`, { columns })
    .then(
      () => Either.right(),
      (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );
}
